import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "./afhc.css";

// ── Static option lists ──────────────────────────────────────────────────────

const AGE_OPTIONS = Array.from({ length: 83 }, (_, i) => i + 1); // 1–83

const STATE_OPTIONS = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

// Replace with API-driven lists when backend is ready
const DISTRICT_OPTIONS = ["District 1", "District 2", "District 3"];
const BLOCK_OPTIONS   = ["Block / Taluka 1", "Block / Taluka 2", "Block / Taluka 3"];

// ── Date range for Date of Registration (last 6 months) ──────────────────────
const today        = dayjs();
const sixMonthsAgo = today.subtract(6, "month");

// ── Yup validation schema ────────────────────────────────────────────────────
const validationSchema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  walk_in: Yup.string().required("Walk-in selection is required"),
  age: Yup.string().required("Age is required"),
  gender: Yup.string().required("Gender is required"),
  marital_status: Yup.string().required("Marital status is required"),
  school_going: Yup.string().required("School going selection is required"),
  guardian_spouse_name: Yup.string().trim(),
  state: Yup.string().required("State is required"),
  district: Yup.string().required("District is required"),
  block_taluka: Yup.string().required("Block / Taluka is required"),
  address: Yup.string().trim().max(100, "Address must be at most 100 characters"),
  contact_number: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number")
    .required("Contact number is required"),
  alternate_contact_number: Yup.string()
    .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number")
    .nullable(),
  date_of_registration: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .required("Date of registration is required")
    .min(sixMonthsAgo.toDate(), "Date cannot be older than six months")
    .max(today.toDate(), "Date cannot be in the future"),
  serial_number: Yup.string().trim(),
  unique_identification_number: Yup.string().trim(),
});

// ── Initial form values ───────────────────────────────────────────────────────
const initialValues = {
  name: "",
  walk_in: "",
  age: "",
  gender: "",
  marital_status: "",
  school_going: "",
  guardian_spouse_name: "",
  state: "",
  district: "",
  block_taluka: "",
  address: "",
  contact_number: "",
  alternate_contact_number: "",
  date_of_registration: "",
  serial_number: "",
  unique_identification_number: "",
};

// ── Helper components ─────────────────────────────────────────────────────────
const Mandatory = () => <span className="afhc_mandate_field">*</span>;

const FieldError = ({ name, errors, touched }) =>
  errors[name] && touched[name] ? (
    <p className="afhc-error">{errors[name]}</p>
  ) : null;

// ── Main component ────────────────────────────────────────────────────────────
const ClientRegistration = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitting(true);
    try {
      // Replace with real AFHC API call
      console.log("AFHC Client Registration payload:", values);
      toast.success("Client registered successfully!");
      resetForm();
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="afhcRegistration">
      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="afhcTitle">
        <h4>Client Registration</h4>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form noValidate>

            {/* ════════════════════════════════════════════════════
                SECTION 1 – Personal Details
            ════════════════════════════════════════════════════ */}
            <div className="feildsSection">
              <div className="afhcSectionHeader">
                <h5>Personal Details</h5>
              </div>
              <div className="afhcFeildsBody">
                <div className="row">

                  {/* Name */}
                  <div className="col-xs-12 col-md-6">
                    <div className="feild">
                      <label>
                        Enter Name <Mandatory />
                      </label>
                      <Field name="name">
                        {({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className="form-control form-text-control"
                            placeholder="Enter Name"
                          />
                        )}
                      </Field>
                      <FieldError name="name" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Age */}
                  <div className="col-xs-12 col-md-3">
                    <div className="feild">
                      <label>
                        Select Age (in years) <Mandatory />
                      </label>
                      <Field name="age">
                        {({ field }) => (
                          <select
                            {...field}
                            className="form-select"
                            value={values.age}
                            onChange={(e) => setFieldValue("age", e.target.value)}
                          >
                            <option value="" disabled>
                              Select Age (in years)
                            </option>
                            {AGE_OPTIONS.map((yr) => (
                              <option key={yr} value={yr}>
                                {yr}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <FieldError name="age" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Gender */}
                  <div className="col-xs-12 col-md-6">
                    <div className="feild">
                      <label>
                        Select Gender <Mandatory />
                      </label>
                      <div className="radioProfile">
                        {["Male", "Female", "Other"].map((opt) => (
                          <label key={opt}>
                            <input
                              type="radio"
                              name="gender"
                              value={opt}
                              checked={values.gender === opt}
                              onChange={() => setFieldValue("gender", opt)}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                      <FieldError name="gender" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Marital Status */}
                  <div className="col-xs-12 col-md-6">
                    <div className="feild">
                      <label>
                        Select Marital Status <Mandatory />
                      </label>
                      <div className="radioProfile">
                        {["Unmarried", "Married"].map((opt) => (
                          <label key={opt}>
                            <input
                              type="radio"
                              name="marital_status"
                              value={opt}
                              checked={values.marital_status === opt}
                              onChange={() => setFieldValue("marital_status", opt)}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                      <FieldError name="marital_status" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* School Going */}
                  <div className="col-xs-12 col-md-6">
                    <div className="feild">
                      <label>
                        School Going <Mandatory />
                      </label>
                      <div className="radioProfile">
                        {["Yes", "No"].map((opt) => (
                          <label key={opt}>
                            <input
                              type="radio"
                              name="school_going"
                              value={opt}
                              checked={values.school_going === opt}
                              onChange={() => setFieldValue("school_going", opt)}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                      <FieldError name="school_going" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Walk-in */}
                  <div className="col-xs-12 col-md-6">
                    <div className="feild">
                      <label>
                        Walk-in <Mandatory />
                      </label>
                      <div className="radioProfile">
                        {["Yes", "No"].map((opt) => (
                          <label key={opt}>
                            <input
                              type="radio"
                              name="walk_in"
                              value={opt}
                              checked={values.walk_in === opt}
                              onChange={() => setFieldValue("walk_in", opt)}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                      <FieldError name="walk_in" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Guardian / Spouse Name */}
                  <div className="col-xs-12 col-md-6">
                    <div className="feild">
                      <label>Enter Guardian's Name / Spouse's Name</label>
                      <Field name="guardian_spouse_name">
                        {({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className="form-control form-text-control"
                            placeholder="Enter Guardian's Name / Spouse's Name"
                          />
                        )}
                      </Field>
                      <FieldError name="guardian_spouse_name" errors={errors} touched={touched} />
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* ════════════════════════════════════════════════════
                SECTION 2 – Location Details
            ════════════════════════════════════════════════════ */}
            <div className="feildsSection">
              <div className="afhcSectionHeader">
                <h5>Location Details</h5>
              </div>
              <div className="afhcFeildsBody">
                <div className="row">

                  {/* State */}
                  <div className="col-xs-12 col-md-4">
                    <div className="feild">
                      <label>
                        Select State <Mandatory />
                      </label>
                      <Field name="state">
                        {({ field }) => (
                          <select
                            {...field}
                            className="form-select"
                            value={values.state}
                            onChange={(e) => setFieldValue("state", e.target.value)}
                          >
                            <option value="" disabled>
                              Select State
                            </option>
                            {STATE_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <FieldError name="state" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* District */}
                  <div className="col-xs-12 col-md-4">
                    <div className="feild">
                      <label>
                        Select District <Mandatory />
                      </label>
                      <Field name="district">
                        {({ field }) => (
                          <select
                            {...field}
                            className="form-select"
                            value={values.district}
                            onChange={(e) => setFieldValue("district", e.target.value)}
                          >
                            <option value="" disabled>
                              Select District
                            </option>
                            {DISTRICT_OPTIONS.map((d) => (
                              <option key={d} value={d}>
                                {d}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <FieldError name="district" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Block / Taluka */}
                  <div className="col-xs-12 col-md-4">
                    <div className="feild">
                      <label>
                        Select Block / Taluka <Mandatory />
                      </label>
                      <Field name="block_taluka">
                        {({ field }) => (
                          <select
                            {...field}
                            className="form-select"
                            value={values.block_taluka}
                            onChange={(e) => setFieldValue("block_taluka", e.target.value)}
                          >
                            <option value="" disabled>
                              Select Block / Taluka
                            </option>
                            {BLOCK_OPTIONS.map((b) => (
                              <option key={b} value={b}>
                                {b}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                      <FieldError name="block_taluka" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="col-xs-12 col-md-12">
                    <div className="feild">
                      <label>Enter Address</label>
                      <Field name="address">
                        {({ field }) => (
                          <textarea
                            {...field}
                            className="areaAddress"
                            placeholder="Enter Address"
                            maxLength={100}
                          />
                        )}
                      </Field>
                      <p className="afhc-char-count">
                        {(values.address || "").length}/100
                      </p>
                      <FieldError name="address" errors={errors} touched={touched} />
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* ════════════════════════════════════════════════════
                SECTION 3 – Contact Details
            ════════════════════════════════════════════════════ */}
            <div className="feildsSection">
              <div className="afhcSectionHeader">
                <h5>Contact Details</h5>
              </div>
              <div className="afhcFeildsBody">
                <div className="row">

                  {/* Contact Number */}
                  <div className="col-xs-12 col-md-6">
                    <div className="feild">
                      <label>
                        Contact Number <Mandatory />
                      </label>
                      <Field name="contact_number">
                        {({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className="form-control form-text-control"
                            placeholder="Enter Contact Number"
                            maxLength={10}
                          />
                        )}
                      </Field>
                      <FieldError name="contact_number" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Alternate Contact Number */}
                  <div className="col-xs-12 col-md-6">
                    <div className="feild">
                      <label>Alternate Contact Number</label>
                      <Field name="alternate_contact_number">
                        {({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className="form-control form-text-control"
                            placeholder="Enter Alternate Contact Number"
                            maxLength={10}
                          />
                        )}
                      </Field>
                      <FieldError name="alternate_contact_number" errors={errors} touched={touched} />
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* ════════════════════════════════════════════════════
                SECTION 4 – Registration Details
            ════════════════════════════════════════════════════ */}
            <div className="feildsSection">
              <div className="afhcSectionHeader">
                <h5>Registration Details</h5>
              </div>
              <div className="afhcFeildsBody">
                <div className="row">

                  {/* Date of Registration */}
                  <div className="col-xs-12 col-md-4">
                    <div className="feild">
                      <label>
                        Date of Registration <Mandatory />
                      </label>
                      <Field name="date_of_registration">
                        {({ field }) => (
                          <input
                            {...field}
                            type="date"
                            className="form-control form-text-control"
                            min={sixMonthsAgo.format("YYYY-MM-DD")}
                            max={today.format("YYYY-MM-DD")}
                          />
                        )}
                      </Field>
                      <FieldError name="date_of_registration" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Serial Number */}
                  <div className="col-xs-12 col-md-4">
                    <div className="feild">
                      <label>Serial Number</label>
                      <Field name="serial_number">
                        {({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className="form-control form-text-control"
                            placeholder="Enter Serial Number"
                          />
                        )}
                      </Field>
                      <FieldError name="serial_number" errors={errors} touched={touched} />
                    </div>
                  </div>

                  {/* Unique Identification Number */}
                  <div className="col-xs-12 col-md-4">
                    <div className="feild">
                      <label>Unique Identification Number</label>
                      <Field name="unique_identification_number">
                        {({ field }) => (
                          <input
                            {...field}
                            type="text"
                            className="form-control form-text-control"
                            placeholder="Enter Unique Identification Number"
                          />
                        )}
                      </Field>
                      <FieldError
                        name="unique_identification_number"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* ── Submit ─────────────────────────────────────────── */}
            <div className="afhcSubmitRow">
              <button
                type="submit"
                className="afhc-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>

          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ClientRegistration;
